import React, {
    FC,
    useState,
} from "react";
import {
    Box,
    Button,
    Drawer,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {AutocompleteGeneric} from "../../table/AutocompleteGeneric";
import {Locale} from "../../../locales/Locale";
import {IdlocaGeneric} from "../../../locales/IdlocaGeneric";
import {useTranslation} from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import {FilterAlt} from "@mui/icons-material";

interface Props {
    typeFilter: string | number | null;
    cityFilter: string | number | null;
    setTypeFilter: (val: string | number | null) => void;
    setCityFilter: (val: string | number | null) => void;
    onFilter: () => void;
    onReset: () => void;
    availableTypeOptions: {id: string; name: string}[];
    availableCityOptions: {id: string; name: string}[];
}

export const GarageTableFiltersMobile: FC<Props> = ({
    typeFilter,
    cityFilter,
    setTypeFilter,
    setCityFilter,
    onFilter,
    onReset,
    availableTypeOptions,
    availableCityOptions,
}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const handleApply = () => {
        onFilter();
        setOpen(false);
    };

    const handleReset = () => {
        onReset();
        setOpen(false);
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    pb: 1,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                {(typeFilter || cityFilter
                ) && (
                    <Typography variant="body2" sx={{color: "text.secondary"}}>
                        <Locale localeId={IdlocaGeneric.filters}/>:
                        {cityFilter && (
                            <>
                                <Locale localeId={IdlocaGeneric.city}/>:{" "}
                                {t(availableCityOptions.find((opt) => opt.id === cityFilter)?.name || "")},
                            </>
                        )}
                        {typeFilter && (
                            <>
                                <Locale localeId={IdlocaGeneric.type}/>:{" "}
                                {t(availableTypeOptions.find((opt) => opt.id === typeFilter)?.name || "")}
                            </>
                        )}
                    </Typography>
                )}
                <Tooltip title={<Locale localeId={IdlocaGeneric.filters} />}>
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(true)}
                        size="small"
                        startIcon={<FilterAlt />}
                    >
                        <Locale localeId={IdlocaGeneric.filters} />
                    </Button>
                </Tooltip>
            </Box>
            <Drawer anchor="bottom" open={open} onClose={() => setOpen(false)}>
                <Box
                    sx={{
                        height: "100vh",
                        p: 2,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        overflowY: "auto",
                        backgroundColor: "background.paper",
                    }}
                >

                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            <Locale localeId={IdlocaGeneric.filter}/>
                        </Typography>
                        <IconButton onClick={() => setOpen(false)}>
                            <CloseIcon/>
                        </IconButton>
                    </Stack>
                    {/* Filters */}
                    <Stack spacing={2}>
                        <AutocompleteGeneric
                            options={availableTypeOptions}
                            value={typeFilter || null}
                            onChange={(val) => setTypeFilter(val ?? null)}
                            getOptionLabel={(id) => {
                                const match = availableTypeOptions.find((opt) => opt.id === id);
                                return match ? t(match.name) : "";
                            }}
                            label={IdlocaGeneric.filter_by_type}
                        />
                        <AutocompleteGeneric
                            options={availableCityOptions}
                            value={cityFilter || null}
                            onChange={(val) => setCityFilter(val ?? null)}
                            getOptionLabel={(id) => {
                                const match = availableCityOptions.find((opt) => opt.id === id);
                                return match ? t(match.name) : "";
                            }}
                            label={IdlocaGeneric.filter_by_city}
                        />
                        <Button variant="contained" onClick={handleApply}>
                            <Locale localeId={IdlocaGeneric.filter}/>
                        </Button>
                        <Button variant="outlined" onClick={handleReset}>
                            <Locale localeId={IdlocaGeneric.reset}/>
                        </Button>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};
